/* @flow */

import * as React from 'react';
import { observable, action } from 'mobx';
import { inject, observer } from 'mobx-react';
import { translate } from 'react-i18next';
import Moment from 'react-moment';
import type { TranslatorProps } from 'react-i18next';
import Grid from '@material-ui/core/Grid/Grid';
import { RouteComponentProps } from 'react-router-dom';
import toCase from 'to-case';

import { getNotificationCopy, generateValues, SENT_REQUEST_TYPES } from 'utils/NotificationUtils';
import Loading from 'components/Loading/Loading';
import FormWrapper from 'components/FormWrapper/FormWrapper';
import PageWrapper from 'components/PageWrapper/PageWrapper';
import { ComponentWithInjectedProps } from 'utils/injectedProps';
import NotificationStore from 'stores/NotificationStore';

import AssetCalibrationRequest from './containers/AssetCalibrationRequest/AssetCalibrationRequest';
import AssetExtensionRequest from './containers/AssetExtensionRequest/AssetExtensionRequest';
import ContractExtensionRequest from './containers/ContractExtensionRequest/ContractExtensionRequest';
import AssetFaultReportRequest from './containers/AssetFaultReportRequest/AssetFaultReportRequest';
import AssetQuoteRequest from './containers/AssetQuoteRequest/AssetQuoteRequest';
import SupportRequest from './containers/SupportRequest/SupportRequest';
import AssetOpportunity from './containers/AssetOpportunity/AssetOpportunity';
import AssetRequest from './containers/AssetRequest/AssetRequest';
import RushAssetRequest from './containers/RushAssetRequest/RushAssetRequest';
import LicenseRequest from './containers/LicenseRequest';
import ReviewPanel from '../NotificationsReview/panels/ReviewPanel/ReviewPanel';

import styles from '../NotificationsReview/NotificationsReview.module.scss';

type InjectedProps = {
  notificationStore: NotificationStore,
} & TranslatorProps &
  RouteComponentProps;

@translate()
@inject('notificationStore')
@observer
export default class SentRequestReview extends ComponentWithInjectedProps<{}, {}, InjectedProps> {
  @observable isLoading = false;
  @observable sentRequest: ?Object = null;

  componentDidMount() {
    this.loadSentRequest();
  }

  getRequestType = (data: Object, type: string) => {
    switch (type) {
      case 'Request Support':
        return <SupportRequest data={data} />;
      case 'Request Asset Rental Extension':
        return <AssetExtensionRequest data={data} />;
      case 'Request Asset Ownership':
        return <div />;
      case 'Request Asset Allocation To Project':
        return <div />;
      case 'Quote Request':
        return <AssetQuoteRequest data={data} />;
      case 'Asset Calibration Request':
        return <AssetCalibrationRequest data={data} />;
      case 'Asset Fault Report':
        return <AssetFaultReportRequest data={data} />;
      case 'Request Contract Extension':
        return <ContractExtensionRequest data={data} />;
      case 'Sub-rental Program Opportunity':
        return <AssetOpportunity data={data} />;
      case 'Consignment Sale Opportunity':
        return <AssetOpportunity data={data} />;
      case 'New Asset Request':
        return <AssetRequest data={data} />;
      case 'New Rush Asset Request':
        return <RushAssetRequest data={data} />;
      case 'New License Request':
        return <LicenseRequest data={data} />;
      default:
        return <div>no data </div>;
    }
  };

  async loadSentRequest() {
    const { notificationStore, match } = this.props;
    this.isLoading = true;
    this.sentRequest = await notificationStore.getSentRequestById(match.params.id);
    this.isLoading = false;
  }

  /**
   * TODO: This function should be temporary. Mauro to provide universal reference to titles.
   *       as used on Notifications. (event_type vs action_name)
   */
  getMainTitle = (actionName: string, eventType: string) => {
    if (actionName === SENT_REQUEST_TYPES.NEW_LICENSE_REQUEST) {
      return toCase.title(eventType || '').replace(/Request/, '') + ' Request';
    }

    return actionName.replace(/Request/, '') + ' Request';
  };

  render() {
    if (this.isLoading || !this.sentRequest) {
      return <Loading />;
    }

    const { t } = this.props;
    const { notification, values } = generateValues(this.sentRequest);
    const bodyCopyInfo = getNotificationCopy(notification, 'Body');

    return (
      <PageWrapper isFlex={false}>
        <FormWrapper
          title={
            t('general:notifications.reviewof') +
            ' ' +
            this.getMainTitle(this.sentRequest.action_name, notification.event_type)
          }
          hoistChildren={'0'}
        >
          <ReviewPanel
            title={t(bodyCopyInfo.key, { ...values, ...bodyCopyInfo.options })}
            date={<Moment date={this.sentRequest.created_at} format="MMMM DD, YYYY - hh:mm a" />}
            extraClass={'sent-request'}
          />
          <Grid className={styles['notifications-grid']} container={true}>
            {this.getRequestType(this.sentRequest, this.sentRequest.action_name)}
          </Grid>
        </FormWrapper>
      </PageWrapper>
    );
  }
}
